import { AppService } from '@services/app/app.service';
import {
  Idle,
  DEFAULT_INTERRUPTSOURCES,
  DocumentInterruptSource,
  StorageInterruptSource
} from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { LabelsService } from '@services/labels/labels.service';
import { NavigatorService } from '@services/navigator/navigator.service';
import { ERRORS } from '@core/models';
import { AuthService } from '@services/auth/auth.service';
import { LoggerService } from '@services/logger/logger.service';
import { TimeInvoking } from '@core/utils';

@Injectable({
  providedIn: 'root'
})
export class IdleService {
  private alert: HTMLIonAlertElement;

  private timedOut: boolean;

  private timeInvoking = new TimeInvoking();

  constructor(
    private idle: Idle,
    private keepalive: Keepalive,
    private appService: AppService,
    private alertController: AlertController,
    private labelsService: LabelsService,
    private navigator: NavigatorService,
    private authService: AuthService,
    private loggerService: LoggerService
  ) {}

  listenIdle(): void {
    if (!this.appService.getConfig().generic.notifyIdle) {
      return;
    }

    this.timeInvoking.invokeOnce('listenIdle', () => {
      this.initWatch();

      this.authService.getAuthUserWhenChange().subscribe(user => {
        // aggiungo timeout per essere sicuro che eventuali modifiche all'app config vengano
        // recepite. trovare un sistema migliore
        setTimeout(() => this.updateWatch(!!user), 100);
      });
    });
  }

  private updateWatch(logged: boolean): void {
    this.configWatch();

    if (logged) {
      this.startWatch();
    } else {
      this.stopWatch();
    }
  }

  /**
   * inizializza il watcher
   */
  private initWatch(): void {
    this.configWatch();
    this.addListeners();
  }

  /**
   * configura il watcher
   */
  private configWatch(): void {
    this.idle.setIdle(this.getConfig().timeout);
    this.idle.setTimeout(this.getConfig().warningTimeout);
    this.idle.setInterrupts(this.getConfig().interrupts);
    this.keepalive.interval(15);
  }

  /**
   * aggiunge i listeners
   */
  private addListeners(): void {
    this.idle.onIdleEnd.subscribe(() => {
      this.userBecomeActive();
    });

    this.idle.onTimeout.subscribe(() => {
      this.userBecomeTimedOut();
    });

    this.idle.onIdleStart.subscribe(() => {
      this.userBecomeIdle();
    });

    this.idle.onTimeoutWarning.subscribe(countdown => {
      this.updateCountdown(countdown);
    });

    this.keepalive.onPing.subscribe(() => {
      console.log('ping');
    });
  }

  /**
   * avvia il wacther
   */
  private startWatch(): void {
    this.timedOut = false;
    this.idle.watch();
  }

  /**
   * interrompe il watch
   */
  private stopWatch(): void {
    this.timedOut = false;
    this.idle.stop();
  }

  /**
   * restituisce la configurazione per l'idle
   */
  private getConfig(): {
    timeout: number;
    warningTimeout: number;
    interrupts: (DocumentInterruptSource | StorageInterruptSource)[];
  } {
    const config = this.appService.getConfig().generic;

    return {
      timeout: config.idleTimeout || 5,
      warningTimeout: config.idleWarningTimeout || 0,
      interrupts: DEFAULT_INTERRUPTSOURCES
    };
  }

  /**
   * gestisce l'utente diventa nuovamente attivo
   * dopo essere stato rilevato come idle
   */
  private userBecomeActive(): void {
    this.hideAlert();
    this.startWatch();

    this.loggerService.logInfo(`The user is back active`);
  }

  /**
   * gestisce quando l'utente diventa idle
   */
  private userBecomeIdle(): void {
    this.showAlert();

    this.loggerService.logInfo(`The user has become idle`);
  }

  /**
   * gestisce quando l'utente è diventato idle
   * e non ha fatto nulla nel tempo di pre avviso
   */
  private userBecomeTimedOut(): void {
    this.timedOut = true;
    this.hideAlert();

    this.loggerService.logInfo(`The user has been logged out after idle`);

    this.navigator.navigateToError(ERRORS.idle);
  }

  /**
   * gestisce il tempo rimanente di pre avviso
   */
  private updateCountdown(countdown: number): void {
    this.updateAlertMessage(countdown);
  }

  /**
   * visualizza l'alert
   */
  private async showAlert(): Promise<void> {
    if (this.getConfig().warningTimeout <= 0) {
      return;
    }

    await this.createAlert();

    // quando il dispositivo è bloccato, la promise di create si risolve solo
    // quando il dispotivo viene sbloccato, e potrebbe essere che non sia più necessario
    // mostrare l'alert
    if (this.alert && !this.timedOut) {
      await this.alert.present();

      this.updateAlertMessage(this.idle.getTimeout());
    }
  }

  /**
   * crea l'alert
   */
  private async createAlert(): Promise<void> {
    this.alert = await this.alertController.create({
      backdropDismiss: false,
      cssClass: 'idle-alert',
      header: this.labelsService.getLabel('msg.idle_title'),
      message: '',
      buttons: [
        {
          text: this.labelsService.getLabel('btn.idle_im_alive'),
          handler: () => this.startWatch(),
          role: 'cancel'
        }
      ]
    });
  }

  /**
   * nasconde l'alert
   */
  private async hideAlert(): Promise<void> {
    if (this.alert) {
      await this.alert.dismiss();
    }
  }

  /**
   * aggiorna il messaggio dell'alert con il valore del countdown
   */
  private updateAlertMessage(countdown: number): void {
    if (this.alert) {
      this.alert.message = this.labelsService.getLabel('msg.idle_message', {
        interpolateParams: { countdown: `${countdown}` }
      });
    }
  }
}
